"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useExplorer, useTenantAtsSystem } from "@/hooks/use-explorer"
import { useEntityTypeDetail } from "@/hooks/use-entity-type-detail"
import { useSchemaPaths, type UseSchemaPathsResult } from "@/hooks/use-schema-paths"
import { isExpandableNode } from "@/lib/ats-schema-expansion"
import { buildColumns, fallbackColumns, nextSort, type ExplorerColumn, type ExplorerSort } from "@/lib/explorer-columns"
import { fieldSelectionStorageKey, frozenColumnsStorageKey, moveField, normalizeSelection, readStoredFrozenColumns, readStoredSelection, sessionSelectionStorage, writeStoredFrozenColumns, writeStoredSelection } from "@/lib/field-selection"
import { getMissingRequiredFilters, toExplorerFilters, withoutFilter, REFERENCE_TYPEAHEAD_THRESHOLD, type FilterValues } from "@/lib/filter-values"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsEntityTypeInfo, AtsFilterDefinition, AtsListSort, AtsRecordPage, AtsReferenceDataItem } from "@/types/ats-explorer-api"

export const EXPLORER_PAGE_SIZE = 25
const LIVE_QUERY_DEBOUNCE_MS = 350
const REFERENCE_SEARCH_DEBOUNCE_MS = 300

/** Why records cannot load right now, for the empty state */
export type LoadBlocker = { kind: "no-entity" } | { kind: "required-filters"; names: string[] } | { kind: "no-fields" } | null

export interface ReferenceBrowseState {
  types: string[]
  selectedType: string | null
  items: AtsReferenceDataItem[]
  /** Server-side matches for clamped sets; null while filtering client-side */
  searchResults: AtsReferenceDataItem[] | null
  filteredItems: AtsReferenceDataItem[]
  isClamped: boolean
  isLoading: boolean
  search: string
  setSearch: (value: string) => void
  select: (type: string) => void
}

export interface ExplorerController {
  atsSystem: string | null
  atsError: string | null
  error: string | null
  clearError: () => void

  entityTypes: AtsEntityTypeInfo[]
  isLoadingTypes: boolean
  /** The listing entry the user picked */
  selectedEntity: AtsEntityTypeInfo | null
  /** The listing entry upgraded with the complete filter set and defaults */
  activeEntity: AtsEntityTypeInfo | null
  isLoadingEntityDetail: boolean
  selectEntity: (entity: AtsEntityTypeInfo) => void

  reference: ReferenceBrowseState

  searchText: string
  setSearchText: (value: string) => void
  filterDefinitions: AtsFilterDefinition[]
  filterValues: FilterValues
  setFilterValues: (values: FilterValues) => void
  removeFilter: (name: string) => void
  clearFilters: () => void
  missingRequiredFilters: AtsFilterDefinition[]

  fieldSchema: UseSchemaPathsResult
  fieldOptions: string[]
  /** The chosen fields in column order */
  selectedFields: string[]
  setSelectedFields: (fields: readonly string[]) => void
  /** Moves a chosen field before another one, or to the end when `beforePath` is null */
  moveSelectedField: (path: string, beforePath: string | null) => void

  sort: ExplorerSort | null
  /** Header click: ascending, then descending, then off */
  toggleSort: (path: string) => void

  /** How many leading columns stay put while the grid scrolls sideways */
  frozenColumns: number
  /** Freezes every column up to and including `path`; 0 columns when `path` is null */
  freezeThrough: (path: string | null) => void

  page: AtsRecordPage | null
  pageNumber: number
  isLoadingRecords: boolean
  blocker: LoadBlocker
  reload: () => void
  nextPage: () => void
  previousPage: () => void
  columns: ExplorerColumn[]
}

/**
 * All explorer state and the live query behind it. Records reload automatically —
 * debounced — whenever the entity, keyword, filters, selected fields or sort change
 * (the Timesheet Hub applies filters live), and stop when a required filter or the
 * field selection is missing. Blades read this through ExplorerContext.
 */
export function useExplorerController(): ExplorerController {
  const { atsSystem, atsSource, error: atsError } = useTenantAtsSystem()
  const { loadEntityTypes, listRecords, loadReferenceDataTypes, loadReferenceData } = useExplorer()
  const { t } = useTranslation()

  const [entityTypes, setEntityTypes] = useState<AtsEntityTypeInfo[]>([])
  const [isLoadingTypes, setIsLoadingTypes] = useState(true)
  const [selectedEntity, setSelectedEntity] = useState<AtsEntityTypeInfo | null>(null)
  const { entity: activeEntity, isLoading: isLoadingEntityDetail } = useEntityTypeDetail(atsSystem, selectedEntity)
  const [error, setError] = useState<string | null>(null)

  const [searchText, setSearchText] = useState("")
  const [filterValues, setFilterValues] = useState<FilterValues>({})
  const [selectedFields, setSelectedFieldsState] = useState<string[]>([])
  const [sort, setSort] = useState<ExplorerSort | null>(null)
  const [frozenColumns, setFrozenColumns] = useState(0)
  // Discovered for every vendor: the picker needs a schema, not vendor-side projection
  const fieldSchema = useSchemaPaths(selectedEntity?.name ?? null, { autoLoad: true, revealPaths: selectedFields })

  const [page, setPage] = useState<AtsRecordPage | null>(null)
  const [pageHistory, setPageHistory] = useState<AtsRecordPage[]>([])
  const [isLoadingRecords, setIsLoadingRecords] = useState(false)
  // Monotonic id of the most recent records load; a slow earlier load can't land
  const requestRef = useRef(0)

  // Reference data browsing
  const [referenceTypes, setReferenceTypes] = useState<string[]>([])
  const [selectedReferenceType, setSelectedReferenceType] = useState<string | null>(null)
  const [referenceItems, setReferenceItems] = useState<AtsReferenceDataItem[]>([])
  const [referenceSearchResults, setReferenceSearchResults] = useState<AtsReferenceDataItem[] | null>(null)
  const [isLoadingReference, setIsLoadingReference] = useState(false)
  const [referenceSearch, setReferenceSearch] = useState("")
  const referenceRequestRef = useRef<string | null>(null)

  useEffect(() => {
    if (!atsSystem) return
    setIsLoadingTypes(true)
    loadEntityTypes(atsSystem)
      .then(setEntityTypes)
      .catch((err) => setError(extractError(err, t("explorer.errors.loadEntityTypes"))))
      .finally(() => setIsLoadingTypes(false))
    loadReferenceDataTypes(atsSystem)
      .then(setReferenceTypes)
      .catch(() => setReferenceTypes([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem])

  const fieldOptions = useMemo(() => {
    const fromSchema = (fieldSchema.fields ?? []).filter((field) => !isExpandableNode(field)).map((field) => field.path)
    return normalizeSelection([...(activeEntity?.defaultFields ?? []), ...fromSchema])
  }, [fieldSchema.fields, activeEntity])

  // Preselect derived default columns once the detail arrives for an entity whose
  // listing carried none (the user may already have picked columns — leave those)
  useEffect(() => {
    if (!activeEntity || activeEntity.defaultFields.length === 0) return
    setSelectedFieldsState((current) => (current.length === 0 ? normalizeSelection(activeEntity.defaultFields) : current))
  }, [activeEntity])

  // Loxo and JobAdder carry no curated defaults, so the columns of the first page become the
  // starting selection: the picker then agrees with the grid, and choosing a field adds to
  // what is on screen instead of replacing it. Once per entity, so Clear stays cleared.
  const seededEntity = useRef<string | null>(null)
  useEffect(() => {
    const entity = selectedEntity?.name
    if (!entity || !page || selectedFields.length > 0 || seededEntity.current === entity) return
    const shown = fallbackColumns(page.records, activeEntity?.sortableFields ?? []).map((column) => column.path)
    if (shown.length === 0) return
    seededEntity.current = entity
    setSelectedFieldsState(shown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedEntity, selectedFields.length, activeEntity])

  // Each entity remembers its columns for the session, so coming back to it shows the same grid
  const selectionStorageKey =
    atsSystem && atsSource && selectedEntity ? fieldSelectionStorageKey(atsSystem, atsSource, selectedEntity.name) : null
  useEffect(() => {
    if (selectionStorageKey) writeStoredSelection(sessionSelectionStorage(), selectionStorageKey, selectedFields)
  }, [selectionStorageKey, selectedFields])

  const frozenStorageKey =
    atsSystem && atsSource && selectedEntity ? frozenColumnsStorageKey(atsSystem, atsSource, selectedEntity.name) : null
  useEffect(() => {
    if (frozenStorageKey) writeStoredFrozenColumns(sessionSelectionStorage(), frozenStorageKey, frozenColumns)
  }, [frozenStorageKey, frozenColumns])

  const setSelectedFields = useCallback(
    (fields: readonly string[]) => {
      // Choosing for oneself settles the entity, so clearing every column is not read as
      // "nothing chosen yet" and answered by seeding the columns straight back
      seededEntity.current = selectedEntity?.name ?? null
      const next = normalizeSelection(fields)
      setSelectedFieldsState(next)
      // A sort by a field that is no longer shown would order the grid invisibly
      setSort((current) => (current && !next.includes(current.path) ? null : current))
    },
    [selectedEntity?.name]
  )

  const moveSelectedField = useCallback((path: string, beforePath: string | null) => setSelectedFieldsState((current) => moveField(current, path, beforePath)), [])

  const filterDefinitions = activeEntity?.filters ?? []
  const missingRequiredFilters = useMemo(() => getMissingRequiredFilters(filterDefinitions, filterValues), [filterDefinitions, filterValues])
  // Vendors that project fields cannot be queried without one; the rest fall back to the record's own keys
  const needsFields = (selectedEntity?.supportsFieldSelection ?? false) && selectedFields.length === 0

  const blocker: LoadBlocker = !selectedEntity
    ? { kind: "no-entity" }
    : missingRequiredFilters.length > 0
      ? { kind: "required-filters", names: missingRequiredFilters.map((f) => f.displayName) }
      : needsFields
        ? { kind: "no-fields" }
        : null

  const requestFields = useCallback(() => (selectedEntity?.supportsFieldSelection ? selectedFields : null), [selectedEntity, selectedFields])
  const requestSort: AtsListSort | null = sort ? { field: sort.path, descending: sort.descending } : null

  const fetchPage = useCallback(
    async (cursor: string | null, history: AtsRecordPage[]) => {
      if (!atsSystem || !selectedEntity) return
      const requestId = ++requestRef.current
      setIsLoadingRecords(true)
      setError(null)
      try {
        const result = await listRecords(atsSystem, {
          entityType: selectedEntity.name,
          searchText: searchText || null,
          filters: toExplorerFilters(filterValues),
          fields: requestFields(),
          sort: requestSort,
          pageSize: EXPLORER_PAGE_SIZE,
          cursor,
        })
        if (requestRef.current !== requestId) return
        setPage(result)
        setPageHistory(history)
      } catch (err) {
        if (requestRef.current !== requestId) return
        setError(extractError(err, t("explorer.errors.loadRecords")))
      } finally {
        if (requestRef.current === requestId) setIsLoadingRecords(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [atsSystem, selectedEntity, searchText, filterValues, requestFields, requestSort?.field, requestSort?.descending]
  )

  // Live query: any change to what is asked for reloads page 1 after a short pause.
  // The key is the request payload, so a filter edit that changes nothing sent
  // (an empty value) does not refetch.
  const queryKey = JSON.stringify({
    entity: selectedEntity?.name ?? null,
    search: searchText,
    filters: toExplorerFilters(filterValues),
    fields: requestFields(),
    sort: requestSort,
  })
  useEffect(() => {
    if (!selectedEntity || selectedReferenceType) return
    if (blocker) {
      requestRef.current++
      setPage(null)
      setPageHistory([])
      setIsLoadingRecords(false)
      return
    }
    const handle = setTimeout(() => void fetchPage(null, []), LIVE_QUERY_DEBOUNCE_MS)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, blocker?.kind, selectedReferenceType])

  const selectEntity = useCallback(
    (entity: AtsEntityTypeInfo) => {
      setSelectedEntity(entity)
      setSelectedReferenceType(null)
      referenceRequestRef.current = null
      requestRef.current++
      setIsLoadingRecords(false)
      setSearchText("")
      setFilterValues({})
      setSort(null)
      setPage(null)
      setPageHistory([])
      const remembered =
        atsSystem && atsSource
          ? readStoredSelection(sessionSelectionStorage(), fieldSelectionStorageKey(atsSystem, atsSource, entity.name))
          : null
      setSelectedFieldsState(remembered ?? normalizeSelection(entity.defaultFields ?? []))
      setFrozenColumns(
        atsSystem && atsSource
          ? readStoredFrozenColumns(sessionSelectionStorage(), frozenColumnsStorageKey(atsSystem, atsSource, entity.name))
          : 0
      )
    },
    [atsSystem, atsSource]
  )

  const selectReferenceType = useCallback(
    (referenceType: string) => {
      if (!atsSystem) return
      setSelectedReferenceType(referenceType)
      setSelectedEntity(null)
      requestRef.current++
      setIsLoadingRecords(false)
      setPage(null)
      setPageHistory([])
      setReferenceSearch("")
      setReferenceItems([])
      setReferenceSearchResults(null)
      setIsLoadingReference(true)
      referenceRequestRef.current = referenceType
      loadReferenceData(atsSystem, referenceType)
        .then((items) => {
          if (referenceRequestRef.current === referenceType) setReferenceItems(items)
        })
        .catch((err) => {
          if (referenceRequestRef.current === referenceType) setError(extractError(err, t("explorer.errors.loadReferenceData")))
        })
        .finally(() => {
          if (referenceRequestRef.current === referenceType) setIsLoadingReference(false)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [atsSystem]
  )

  const referenceIsClamped = referenceItems.length >= REFERENCE_TYPEAHEAD_THRESHOLD

  // Clamped sets are searched server-side as the user types (debounced)
  useEffect(() => {
    const query = referenceSearch.trim()
    if (!atsSystem || !selectedReferenceType || !referenceIsClamped || query === "") {
      setReferenceSearchResults(null)
      return
    }
    let cancelled = false
    const referenceType = selectedReferenceType
    const handle = setTimeout(() => {
      loadReferenceData(atsSystem, referenceType, query)
        .then((items) => {
          if (!cancelled) setReferenceSearchResults(items)
        })
        .catch(() => {
          if (!cancelled) setReferenceSearchResults([])
        })
    }, REFERENCE_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceSearch, selectedReferenceType, referenceIsClamped, atsSystem])

  const filteredReferenceItems = useMemo(() => {
    if (referenceSearchResults) return referenceSearchResults
    const query = referenceSearch.trim().toLowerCase()
    if (!query) return referenceItems
    return referenceItems.filter((item) => item.name.toLowerCase().includes(query) || item.id.toLowerCase().includes(query))
  }, [referenceItems, referenceSearch, referenceSearchResults])

  // The chosen fields are the columns; with none chosen the record's own keys stand in
  const columns = useMemo(
    () =>
      selectedFields.length > 0
        ? buildColumns(selectedFields, fieldSchema.fields, activeEntity?.sortableFields ?? [])
        : fallbackColumns(page?.records ?? [], activeEntity?.sortableFields ?? []),
    [selectedFields, fieldSchema.fields, activeEntity, page]
  )

  return {
    atsSystem,
    atsError,
    error,
    clearError: () => setError(null),
    entityTypes,
    isLoadingTypes,
    selectedEntity,
    activeEntity,
    isLoadingEntityDetail,
    selectEntity,
    reference: {
      types: referenceTypes,
      selectedType: selectedReferenceType,
      items: referenceItems,
      searchResults: referenceSearchResults,
      filteredItems: filteredReferenceItems,
      isClamped: referenceIsClamped,
      isLoading: isLoadingReference,
      search: referenceSearch,
      setSearch: setReferenceSearch,
      select: selectReferenceType,
    },
    searchText,
    setSearchText,
    filterDefinitions,
    filterValues,
    setFilterValues,
    removeFilter: (name) => setFilterValues((current) => withoutFilter(current, name)),
    clearFilters: () => setFilterValues({}),
    missingRequiredFilters,
    fieldSchema,
    fieldOptions,
    selectedFields,
    setSelectedFields,
    moveSelectedField,
    sort,
    toggleSort: (path) => setSort((current) => nextSort(current, path)),
    // One column is always left scrolling: freezing through the last one — by choosing it,
    // or by dropping columns until an earlier boundary reaches the end — would otherwise
    // pin the whole grid, with nothing moving to show that anything is frozen
    frozenColumns: Math.min(frozenColumns, Math.max(columns.length - 1, 0)),
    freezeThrough: (path) => setFrozenColumns(path === null ? 0 : columns.findIndex((column) => column.path === path) + 1),
    page,
    pageNumber: pageHistory.length + 1,
    isLoadingRecords,
    blocker,
    reload: () => void fetchPage(null, []),
    nextPage: () => {
      if (page?.nextCursor) void fetchPage(page.nextCursor, [...pageHistory, page])
    },
    previousPage: () => {
      if (pageHistory.length > 0) {
        setPage(pageHistory[pageHistory.length - 1])
        setPageHistory(pageHistory.slice(0, -1))
      }
    },
    columns,
  }
}

export function extractError(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "errorData" in err) {
    const detail = (err as { errorData?: { detail?: string } }).errorData?.detail
    if (detail) return detail
  }
  return err instanceof Error ? err.message : fallback
}
